import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../utils/api';
import './HealthProgramApplicationForm.css';

const getQuestionKey = (sectionIdx, questionIdx) => `${sectionIdx}-${questionIdx}`;
const normalizeConditionValue = (value) => String(value ?? '').trim().toLowerCase();

const HealthProgramApplicationForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);
  const [program, setProgram] = useState(null);
  const [answers, setAnswers] = useState({});
  const [files, setFiles] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [existingApplication, setExistingApplication] = useState(null);

  const getQuestionMetadata = useCallback(() => {
    const questionById = {};

    if (!program?.sections) {
      return { questionById };
    }

    program.sections.forEach((section, sectionIdx) => {
      (section.questions || []).forEach((question, questionIdx) => {
        if (!question?.questionId) return;

        questionById[question.questionId] = {
          question,
          key: getQuestionKey(sectionIdx, questionIdx)
        };
      });
    });

    return { questionById };
  }, [program]);

  const isQuestionVisible = useCallback((question) => {
    if (!question?.conditional?.enabled) return true;

    const parentQuestionId = question?.conditional?.parentQuestionId;
    if (!parentQuestionId) return true;

    const { questionById } = getQuestionMetadata();
    const parentMeta = questionById[parentQuestionId];

    if (!parentMeta) return true;

    const parentQuestion = parentMeta.question;
    const parentAnswer = answers[parentMeta.key];
    const triggerValue = question?.conditional?.showWhenValue;
    const normalizedTrigger = normalizeConditionValue(triggerValue);

    if (parentQuestion?.type === 'checkbox') {
      if (normalizedTrigger === 'true' || normalizedTrigger === 'yes' || normalizedTrigger === '1') {
        return parentAnswer === true;
      }
      if (normalizedTrigger === 'false' || normalizedTrigger === 'no' || normalizedTrigger === '0') {
        return parentAnswer === false;
      }

      return Boolean(parentAnswer);
    }

    if (parentQuestion?.type === 'multi-select') {
      return Array.isArray(parentAnswer) && parentAnswer.some((value) => normalizeConditionValue(value) === normalizedTrigger);
    }

    return normalizeConditionValue(parentAnswer) === normalizedTrigger;
  }, [answers, getQuestionMetadata]);

  const fetchProgram = useCallback(async () => {
    try {
      const response = await api.get(`/api/health-programs/${id}`);
      setProgram(response.data.program);
      
      // Check if user has already applied
      try {
        const applicationsResponse = await api.get(`/api/health-program-applications?programId=${id}&userOnly=true`);
        if (applicationsResponse.data.applications && applicationsResponse.data.applications.length > 0) {
          setExistingApplication(applicationsResponse.data.applications[0]);
        }
      } catch (appErr) {
        console.error('Error checking existing applications:', appErr);
      }
      
      // Initialize answers
      const initAnswers = {};
      if (response.data.program.sections) {
        response.data.program.sections.forEach((section, sIdx) => {
          section.questions.forEach((q, qIdx) => {
            const key = getQuestionKey(sIdx, qIdx);
            if (q.type === 'multi-select') {
              initAnswers[key] = [];
            } else if (q.type === 'checkbox') {
              initAnswers[key] = false;
            } else {
              initAnswers[key] = '';
            }
          });
        });
      }
      setAnswers(initAnswers);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching program:', err);
      setError(err.response?.data?.message || 'Error fetching program details');
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProgram();
  }, [fetchProgram]);

  const handleChange = (key, value) => {
    setAnswers({ ...answers, [key]: value });
    // Clear validation error for this field
    if (validationErrors[key]) {
      const newErrors = { ...validationErrors };
      delete newErrors[key];
      setValidationErrors(newErrors);
    }
  };

  const handleFileChange = (questionKey, file) => {
    if (file) {
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/jpeg',
        'image/png'
      ];
      
      const fileName = file.name.toLowerCase();
      const validExtensions = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png'];
      const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));
      
      if (!allowedTypes.includes(file.type) && !hasValidExtension) {
        alert('Invalid file type. Only PDF, DOC, DOCX, JPG, and PNG files are allowed.');
        return;
      }
      
      setFiles({ ...files, [questionKey]: file });
      handleChange(questionKey, file.name);
    } else {
      const updatedFiles = { ...files };
      delete updatedFiles[questionKey];
      setFiles(updatedFiles);
      handleChange(questionKey, '');
    }
  };

  const handleMultiSelectChange = (key, option, checked) => {
    let current = Array.isArray(answers[key]) ? answers[key] : [];
    if (checked) {
      if (!current.includes(option)) current.push(option);
    } else {
      current = current.filter(item => item !== option);
    }
    handleChange(key, current);
  };

  const validateForm = () => {
    const errors = {};
    
    if (!program || !program.sections) return errors;

    program.sections.forEach((section, sIdx) => {
      section.questions.forEach((question, qIdx) => {
        if (!isQuestionVisible(question)) {
          return;
        }

        const key = getQuestionKey(sIdx, qIdx);
        const answer = answers[key];

        if (question.required) {
          if (question.type === 'multi-select') {
            if (!Array.isArray(answer) || answer.length === 0) {
              errors[key] = 'This field is required';
            }
          } else if (!answer || (typeof answer === 'string' && answer.trim() === '')) {
            errors[key] = 'This field is required';
          }
        }
      });
    });

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      alert('Please fill in all required fields');
      window.scrollTo(0, 0);
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const portal = program.category || 'health';
      const normalizedProgramType = Array.isArray(program.programType)
        ? program.programType.filter(Boolean)
        : (program.programType ? [program.programType] : []);

      if (normalizedProgramType.length === 0) {
        setError('This program is missing a program type. Please contact support before applying.');
        setSubmitting(false);
        return;
      }

      const applicationData = {
        applicantName: `${currentUser.firstName} ${currentUser.lastName}`,
        healthProgramId: program._id,
        programTitle: program.title,
        programType: normalizedProgramType,
        portal,
        answers
      };

      const response = await api.post('/api/health-program-applications', applicationData);
      const applicationId = response.data.application?._id;

      // Upload files if any
      if (Object.keys(files).length > 0) {
        const uploadErrors = [];

        await Promise.all(Object.entries(files).map(async ([key, file]) => {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('applicationId', applicationId);
          formData.append('questionKey', key);

          try {
            await api.post('/api/files/upload-application', formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          } catch (uploadErr) {
            console.error('File upload error:', uploadErr);
            const errorMessage = uploadErr.response?.data?.message || uploadErr.message || 'Upload failed';
            uploadErrors.push(`${file.name}: ${errorMessage}`);
          }
        }));

        if (uploadErrors.length > 0) {
          if (applicationId) {
            try {
              await api.delete(`/api/health-program-applications/${applicationId}`);
            } catch (rollbackErr) {
              console.error('Error rolling back application after failed file upload:', rollbackErr);
            }
          }

          const failureMessage = `Your application was not submitted because one or more files could not be uploaded:\n\n${uploadErrors.join('\n')}\n\nPlease try again.`;
          setError(failureMessage);
          setSubmitting(false);
          window.scrollTo(0, 0);
          alert(failureMessage);
          return;
        }
      }

      alert('Application submitted successfully!');
      navigate('/dashboard');
    } catch (err) {
      console.error('Submission error:', err);
      const errorMessage = err.response?.data?.message || 'Error submitting application';
      setError(errorMessage);
      
      // If duplicate application error, refresh to show the existing application notice
      if (err.response?.data?.existingApplicationId) {
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
      
      setSubmitting(false);
      window.scrollTo(0, 0);
    }
  };

  const renderQuestion = (question, sectionIdx, questionIdx) => {
    if (!isQuestionVisible(question)) {
      return null;
    }

    const key = getQuestionKey(sectionIdx, questionIdx);
    const answer = answers[key];
    const hasError = validationErrors[key];

    return (
      <div key={key} className={`question-block ${hasError ? 'has-error' : ''}`}>
        <label className="question-label">
          {question.label}
          {question.required && <span className="required">*</span>}
        </label>
        
        {question.helpText && (
          <p className="help-text">{question.helpText}</p>
        )}

        {question.type === 'text' && (
          <input
            type="text"
            value={answer || ''}
            onChange={(e) => handleChange(key, e.target.value)}
            placeholder={question.placeholder}
            className="form-input"
          />
        )}

        {question.type === 'textarea' && (
          <textarea
            value={answer || ''}
            onChange={(e) => handleChange(key, e.target.value)}
            placeholder={question.placeholder}
            rows={question.rows || 4}
            className="form-textarea"
          />
        )}

        {question.type === 'number' && (
          <input
            type="number"
            value={answer || ''}
            onChange={(e) => handleChange(key, e.target.value)}
            placeholder={question.placeholder}
            className="form-input"
          />
        )}

        {question.type === 'date' && (
          <input
            type="date"
            value={answer || ''}
            onChange={(e) => handleChange(key, e.target.value)}
            className="form-input"
          />
        )}

        {question.type === 'dropdown' && (
          <select
            value={answer || ''}
            onChange={(e) => handleChange(key, e.target.value)}
            className="form-select"
          >
            <option value="">-- Select an option --</option>
            {question.options && question.options.map((option, idx) => (
              <option key={idx} value={option}>{option}</option>
            ))}
          </select>
        )}

        {question.type === 'checkbox' && (
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={answer || false}
              onChange={(e) => handleChange(key, e.target.checked)}
            />
            <span>{question.checkboxLabel || 'Yes'}</span>
          </label>
        )}

        {question.type === 'multi-select' && (
          <div className="checkbox-group">
            {question.options && question.options.map((option, idx) => (
              <label key={idx} className="checkbox-label">
                <input
                  type="checkbox"
                  checked={Array.isArray(answer) && answer.includes(option)}
                  onChange={(e) => handleMultiSelectChange(key, option, e.target.checked)}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        )}

        {question.type === 'file' && (
          <input
            type="file"
            onChange={(e) => handleFileChange(key, e.target.files[0])}
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            className="form-file"
          />
        )}

        {hasError && <span className="error-text">{validationErrors[key]}</span>}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="health-application-container">
        <div className="loading">Loading program details...</div>
      </div>
    );
  }

  if (error && !program) {
    return (
      <div className="health-application-container">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  if (!program) {
    return (
      <div className="health-application-container">
        <div className="error-message">Program not found</div>
      </div>
    );
  }

  return (
    <div className="health-application-container">
      <div className="application-header">
        <h1>Apply for {program.title}</h1>
        <div className="program-info">
          <span className="program-type">{program.programType}</span>
          <span className="program-target">{Array.isArray(program.targetPopulation) ? program.targetPopulation.join(', ') : program.targetPopulation}</span>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {existingApplication && (
        <div className="existing-application-notice">
          <h3>✓ Application Already Submitted</h3>
          <p>You have already submitted an application for this program.</p>
          <div className="application-status">
            <strong>Status:</strong> <span className={`status-badge status-${existingApplication.status}`}>
              {existingApplication.status.charAt(0).toUpperCase() + existingApplication.status.slice(1)}
            </span>
          </div>
          <div className="application-actions">
            <button 
              onClick={() => navigate(`/review-health-application/${existingApplication._id}?view=applicant`)}
              className="btn-view-application"
            >
              View Your Application
            </button>
            <button 
              onClick={() => navigate(`/health-programs${program?.category ? `?category=${program.category}` : ''}`)}
              className="btn-back"
            >
              Back to Programs
            </button>
          </div>
        </div>
      )}

      {!existingApplication && (
        <>
          {program.description && (
            <div className="program-description">
              <h3>About This Program</h3>
              <div dangerouslySetInnerHTML={{ __html: program.description }} />
            </div>
          )}

          {program.eligibilityCriteria && (
            <div className="eligibility-section">
              <h3>Eligibility Requirements</h3>
              <div dangerouslySetInnerHTML={{ __html: program.eligibilityCriteria }} />
            </div>
          )}

          <form onSubmit={handleSubmit} className="application-form">
            {program.sections && program.sections.length > 0 ? (
              program.sections.map((section, sIdx) => (
                <div key={sIdx} className="form-section">
                  <h2 className="section-title">{section.title || section.label}</h2>
                  {section.description && (
                    <p className="section-description">{section.description}</p>
                  )}
                  <div className="questions-container">
                    {section.questions.map((question, qIdx) =>
                      renderQuestion(question, sIdx, qIdx)
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="no-sections">
                <p>This program does not have an application form configured yet. Please contact the program administrator.</p>
              </div>
            )}

            {program.sections && program.sections.length > 0 && (
              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => navigate(`/health-programs${program?.category ? `?category=${program.category}` : ''}`)}
                  className="btn-cancel"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-submit"
                >
                  {submitting ? 'Submitting...' : 'Submit Application'}
                </button>
              </div>
            )}
          </form>
        </>
      )}
    </div>
  );
};

export default HealthProgramApplicationForm;
